import { Button } from '@/components/ui/button';
import { X, ChevronDown, Calendar, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Column } from '@tanstack/react-table';
import type { FilterValue, FilterType, SelectOption } from './types';
import { FilterConfigDialog } from './FilterConfigDialog';
import { useLingui } from '@lingui/react/macro';
import { formatDateDisplay, formatDateRange } from '@/lib/format';

interface FilterTagProps<T = unknown> {
  columnLabel: string;
  filterValue: FilterValue;
  column: Column<T>;
  filterType: FilterType;
  selectOptions?: SelectOption[];
  onChange: (value: FilterValue) => void;
  onRemove: () => void;
  className?: string;
}

/**
 * Tag component that displays an active filter with edit and remove actions
 * Shows a compact representation of the filter with the column name and current value
 */
export function FilterTag<T>({
  columnLabel,
  filterValue,
  column,
  filterType,
  selectOptions,
  onChange,
  onRemove,
  className,
}: FilterTagProps<T>) {
  const { i18n } = useLingui();
  // Format the filter value for display
  const formatFilterValue = (value: FilterValue): string => {
    if (!value) return '';
    
    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      
      // If we have select options, convert values to labels
      if (selectOptions && selectOptions.length > 0) {
        const labels = value.map(val => {
          const option = selectOptions.find(opt => opt.value === val);
          return option ? option.label : val;
        });
        
        if (labels.length === 1) return labels[0];
        return `${labels.length} selected`;
      }
      
      if (value.length === 1) return value[0];
      return `${value.length} selected`;
    }
    
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      
      // Handle number range
      if ('min' in obj || 'max' in obj) {
        const min = obj.min as number | undefined;
        const max = obj.max as number | undefined;
        if (min !== undefined && max !== undefined) {
          return `${min} - ${max}`;
        }
        if (min !== undefined) {
          return `≥ ${min}`;
        }
        if (max !== undefined) {
          return `≤ ${max}`;
        }
      }
      
      // Handle date range
      if ('from' in obj || 'to' in obj) {
        const from = obj.from as Date | undefined;
        const to = obj.to as Date | undefined;
        if (from && to) {
          return formatDateRange(from, to, i18n.locale);
        }
        if (from) {
          return i18n._({ id: 'table.filter.dateFrom', message: 'From {date}' }, { date: formatDateDisplay(from) });
        }
        if (to) {
          return i18n._({ id: 'table.filter.dateUntil', message: 'Until {date}' }, { date: formatDateDisplay(to) });
        }
      }
    }
    
    // For single string values, check if we have a matching select option
    if (typeof value === 'string' && selectOptions && selectOptions.length > 0) {
      const option = selectOptions.find(opt => opt.value === value);
      return option ? option.label : value;
    }
    
    return String(value);
  };

  const displayValue = formatFilterValue(filterValue);
  
  // Don't render if no meaningful value
  if (!displayValue) {
    return null;
  }

  // Determine the icon based on filter type and column name
  const getFilterIcon = () => {
    if (filterType === 'date') {
      return <Calendar className="h-3 w-3" />;
    }
    if (filterType === 'select') {
      // Check if it's a person/name field
      const columnId = column.id.toLowerCase();
      if (columnId.includes('name') || columnId.includes('person') || columnId.includes('employee')) {
        return <User className="h-3 w-3" />;
      }
      return <ChevronDown className="h-3 w-3" />;
    }
    return null;
  };

  const icon = getFilterIcon();

  return (
    <FilterConfigDialog
      column={column}
      filterType={filterType}
      columnLabel={columnLabel}
      value={filterValue}
      onChange={onChange}
      onClear={onRemove}
      selectOptions={selectOptions}
      trigger={
        <div
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium cursor-pointer',
            'bg-primary/10 text-primary border border-primary/20 rounded-md',
            'hover:bg-primary/20 transition-colors',
            className
          )}
        >
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span className="font-semibold">{columnLabel}:</span>
          <span className="truncate max-w-24" title={displayValue}>
            {displayValue}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="h-4 w-4 p-0 hover:bg-destructive/20 hover:text-destructive ml-1"
            aria-label={i18n._({
              id: 'table.filter.remove',
              message: 'Remove the {column} filter',
              values: { column: columnLabel },
            })}
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </Button>
        </div>
      }
    />
  );
}